import { useAuthenticationService } from "../../services/authentication.service";
import { useNavigate } from "react-router-dom";
import logger from "../../utils/logger";
import { Image } from "rsuite";
import BestStartButton from "../../components/button";
import "./login.css";
import { BESTSTART_LOGO_WHITE } from "../../utils/constants/images";
import roles from "../../utils/constants/roles";
import { useEffect } from "react";
import ToastService from "../../services/toast.service";
import { useSSO } from "@best-start/beststart.react.app-switcher";

const UNAUTHORIZED_LOGOUT_REASON = 'winz_unauthorized_logout';

function Login() {
  const navigate = useNavigate();
  const auth = useAuthenticationService();
  const roles_config = { ALL_ROLES: roles.ALL_ROLES };
  const { ssoStatus } = useSSO(auth, navigate, roles_config);

  useEffect(() => {
    if (window.sessionStorage.getItem(UNAUTHORIZED_LOGOUT_REASON) === 'true') {
      window.sessionStorage.removeItem(UNAUTHORIZED_LOGOUT_REASON);
      ToastService.error("You are not authorized to access this application", "Access Denied");
    }
  }, []);

  useEffect(() => {
    async function initializeLogin() {
      logger.log("Login page loaded");
      if (!auth || ssoStatus.isChecking) {
        return;
      }

      try {
        await auth.initializeSession();
        await auth.completeLogin();

        if (auth.isAuthenticated() && auth.isInAnyRole(roles.ALL_ROLES)) {
          navigate("/");
          return;
        }

        if (auth.isAuthenticated() && !auth.isInAnyRole(roles.ALL_ROLES)) {
          logger.error("Login failed - user has no authorized roles");
          window.sessionStorage.setItem(UNAUTHORIZED_LOGOUT_REASON, 'true');
          await auth.logout();
          return;
        }
      } catch (error) {
        logger.error("Error initializing backend login session", error);
      }
    }

    initializeLogin();
  }, [auth, navigate, ssoStatus.isChecking]);

  if (ssoStatus.isChecking) {
    return (
      <div className="login-container login-shell">
        <div className="login-shell-panel" role="status" aria-live="polite">
          <Image src={BESTSTART_LOGO_WHITE} className="login-logo login-shell-logo" width={400} />
          <button className="it-button login-shell-title" data-text="WINZ Portal">
            <span className="actual-text">&nbsp;WINZ&nbsp;Portal&nbsp;</span>
            <span aria-hidden="true" className="hover-text">
              &nbsp;WINZ&nbsp;Portal&nbsp;
            </span>
          </button>
          <div className="sso-shell-copy">
            <p className="sso-shell-eyebrow">Single sign-on in progress</p>
            <h1>Signing you in...</h1>
            <p>Picking up your session from {ssoStatus.sourceApp || "another app"} and finishing sign-in.</p>
          </div>
          <div className="sso-shell-progress" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-inner-container">
        <Image src={BESTSTART_LOGO_WHITE} className="login-logo" width={400} />
        <button className="it-button" data-text="WINZ Portal">
          <span className="actual-text">&nbsp;WINZ&nbsp;Portal&nbsp;</span>
          <span aria-hidden="true" className="hover-text">
            &nbsp;WINZ&nbsp;Portal&nbsp;
          </span>
        </button>

        {ssoStatus.isChecking && (
          <div className="sso-status">
            <p>Signing you in from {ssoStatus.sourceApp || "another app"}...</p>
          </div>
        )}

        {ssoStatus.error && !ssoStatus.isChecking && (
          <div className="sso-error">
            <p>Single sign-on failed. Please sign in manually.</p>
          </div>
        )}

        <div className="login-form">
          {auth && !ssoStatus.isChecking && <BestStartButton text="Login" color="green" width={200} height={50} onClick={() => auth.login(window.location.href)} />}
        </div>
      </div>
    </div>
  );
}
export default Login;
